import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Stack, TextField, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import emptyKnowledge from '@/assets/New-images/empty-knowledge.svg'
import loadingimg from '@/assets/images/portal-loading-anim.svg'
import { HelperService } from '@/services/helper.service'
import KnowledgeService from '@/services/KnowledgeService'
import CustomCard from '@/components/UI/CustomCard'
import BasicDrawer from '@/components/UI/Drawer'

const helperService = new HelperService()
const knowledgeService = new KnowledgeService()

interface LessonPlanProps {
  groupId?: string
}

const LessonPlan: React.FC<LessonPlanProps> = ({ groupId }) => {
  const { t } = useTranslation('translation')
  const [lessonPlanText, setLessonPlanText] = useState<string>('')
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)

  // Fetch lesson plan for the group when component mounts
  const fetchLessonPlan = async (groupId: string) => {
    setIsLoading(true)
    const response = await knowledgeService.getLessonPlan(groupId)
    if (response?.data.data?.lesson_plan?.lesson_plan) {
      setLessonPlanText(response.data.data.lesson_plan.lesson_plan)
    } else {
      setLessonPlanText('')
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (groupId) {
      fetchLessonPlan(groupId)
    }
  }, [groupId])

  const handleSave = async () => {
    if (!groupId) return

    setIsSaving(true)
    await knowledgeService.updateLessonPlan(groupId, lessonPlanText)
    helperService.showToaster(t('Lesson plan saved successfully'), 'success')
    setIsEditDrawerOpen(false)
    setIsSaving(false)
  }

  const handleOpenDrawer = () => {
    setIsEditDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setIsEditDrawerOpen(false)
    // Reset to original content if user cancels
    if (groupId) {
      fetchLessonPlan(groupId)
    }
  }

  return (
    <>
      <BasicDrawer
        open={isEditDrawerOpen}
        onClose={handleCloseDrawer}
        title={t('Edit Lesson Plan')}
      >
        <TextField
          fullWidth
          multiline
          rows={20}
          value={lessonPlanText}
          onChange={e => setLessonPlanText(e.target.value)}
          placeholder={t(
            'Enter your lesson plan here. You can include objectives, activities, assessments, and resources...',
          )}
          variant="outlined"
        />

        <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('Saving...') : t('Save')}
          </Button>
          <Button variant="outlined" onClick={handleCloseDrawer}>
            {t('Cancel')}
          </Button>
        </Stack>
      </BasicDrawer>

      <div className="learning">
        <div className="tw-flex tw-justify-end tw-items-center tw-mb-2">
          <Button
            onClick={handleOpenDrawer}
            startIcon={lessonPlanText ? <EditIcon fontSize="small" /> : null}
          >
            {lessonPlanText ? t('Edit') : t('+ Add')}
          </Button>
        </div>

        {!lessonPlanText && (
          <div className="empty-knowledge text-center my-2">
            <h3 className="h6 clr-grey-txt7 mb-3">
              {t('You have yet to create a lesson plan for this group')}
            </h3>
            <figure className="h-auto">
              {isLoading ? (
                <img src={loadingimg} alt="" />
              ) : (
                <img src={emptyKnowledge} alt="" />
              )}
            </figure>
            {!isLoading && (
              <button className="btn btn-link m-2" onClick={handleOpenDrawer}>
                {t('Click here to get started')}
              </button>
            )}
          </div>
        )}

        {lessonPlanText && (
          <>
            <CustomCard className="tw-p-8">
              <Typography className="tw-whitespace-pre-line" variant="body2">
                {lessonPlanText}
              </Typography>
            </CustomCard>
          </>
        )}
      </div>
    </>
  )
}

export default LessonPlan
